/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */

import moment from 'moment';

export const uniqueId = () => `_${Math.random().toString(36).slice(2, 11)}`;

export const throttle = (callback: Function, container: any, wait: number) => {
  let time: number = Date.now();
  if (Date.now() - time - wait > 0) {
    callback();
    time = Date.now();
  } else {
    clearTimeout(container.current.timeout);
    container.current.timeout = setTimeout(() => {
      time = Date.now();
      callback();
    }, wait);
  }
};

export const dateValidatorFormatter = (date: string | undefined, formatString: string) => {
  const momentToFormat: any = moment(date);
  if (momentToFormat.isValid()) return momentToFormat.format(formatString);
  return null;
};

export const parametersAsObject = (
  parameters: URLSearchParams,
): { [key: string]: string | null } => {
  const parameterObject: { [key: string]: string | null } = {};
  const keys: any = parameters.keys();
  for (const value of keys) {
    parameterObject[value] = parameters.get(value);
  }
  return parameterObject;
};

export const urlUpdate = (
  history: any,
  parametersObject: { [key: string]: string | null },
  updateValues: { [key: string]: string | null },
) => {
  let parameters = '';
  const updatedParametersObject = { ...parametersObject };
  Object.keys(updateValues).forEach((key) => {
    if (updateValues[key]) updatedParametersObject[key] = updateValues[key];
    else if (updatedParametersObject[key]) delete updatedParametersObject[key];
  });
  const { fileTypeIdFilters, excludeFileTypeIdFilters } = updatedParametersObject;
  if (fileTypeIdFilters && excludeFileTypeIdFilters) {
    delete updatedParametersObject.fileTypeIdFilters;
  }
  Object.keys(updatedParametersObject).forEach((key: string, index: number) => {
    const prefix = index === 0 ? '?' : '&';
    if (updatedParametersObject[key]) {
      parameters += `${prefix}${key}=${updatedParametersObject[key]}`;
    }
  });
  history.push(`${history.location.pathname}${parameters}`);
};

export const fileToBase64 = (file: any): string | unknown =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => resolve(reader.result));
    reader.addEventListener('error', (error) => reject(error));
  });

export const removeDuplicateArrayStrings = (array: string[]) =>
  array.filter((item, index) => array.indexOf(item) === index);

export const poll = async ({
  callBack,
  errorCallback,
  validate,
  interval,
  maxAttempts,
}: {
  callBack: Function;
  errorCallback?: Function;
  validate: Function;
  interval: number;
  maxAttempts: any;
}) => {
  let attempts = 0;
  const executePoll: any = async (
    resolve: (argument: any) => any,
    reject: (argument: Error) => any,
  ) => {
    try {
      const result = await callBack();
      attempts += 1;
      if (validate(result)) {
        return resolve(result);
      }
      if (maxAttempts && attempts === maxAttempts) {
        return reject(new Error('Exceeded max attempts'));
      }
    } catch {
      if (errorCallback) errorCallback();
    }
    return setTimeout(executePoll, interval, resolve, reject);
  };
  return new Promise(executePoll);
};

export const createChunks = (file: File, maxMb: number) => {
  const maxBytes = maxMb * 1000000;
  let startPointer = 0;
  const endPointer = file.size;
  const chunks = [];
  while (startPointer < endPointer) {
    const newStartPointer = startPointer + maxBytes;
    chunks.push(file.slice(startPointer, newStartPointer));
    startPointer = newStartPointer;
  }
  return chunks;
};

export const downloadFromUrl = (url: string, fileNameProvided?: string) => {
  const a = document.createElement('a');
  const fileName = url.split('/').pop();
  a.href = url;
  a.download = fileName || fileNameProvided || '';
  document.body.append(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

export const chunkArray = (array: any[], chunkSize: number) => {
  if (chunkSize <= 0) throw new Error('Invalid chunk size');
  const ResultsArray = [];
  for (let index = 0, { length } = array; index < length; index += chunkSize)
    ResultsArray.push(array.slice(index, index + chunkSize));
  return ResultsArray;
};

export const formatDate = (dateFormat: any, timeFormat: any) => {
  let transformedDate = '';
  let transformedTime = '';
  if (dateFormat === 'mmddyyyy') {
    transformedDate = 'MM/DD/YYYY';
  } else if (dateFormat === 'ddmmyyyy') {
    transformedDate = 'DD/MM/YYYY';
  } else {
    transformedDate = 'MMMM DD, YYYY';
  }
  if (timeFormat === 'hours12') {
    transformedTime = 'hh:mm:ss A';
  } else {
    transformedTime = 'HH:mm:ss';
  }
  const formatString = `${transformedDate} ${transformedTime}`;
  return formatString;
};

export const stringToBlob = (text: string, contentType: string) =>
  new Blob([text], { type: contentType });

export const fileContentsToBlob = (file: any): string | unknown =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.addEventListener('load', () => resolve(stringToBlob(`${reader.result}`, file.type)));
    reader.addEventListener('error', (error) => reject(error));
  });

export const CSVToArray = (CSV_string: string, delimiter: string) => CSV_string.split(delimiter);

export const timeout = (pauseTime: number) =>
  new Promise((resolve) => setTimeout(resolve, pauseTime));

export const sleep = async (callback: Function, callbackInput: any, pauseTime: number) => {
  await timeout(pauseTime);
  return callback(callbackInput);
};

export const retryWithInterval = async (
  callback: Function,
  callbackInput: any,
  pauseTime: number,
  retryCount: number,
) => {
  let result;
  let iterator = 0;
  while (iterator < retryCount) {
    // eslint-disable-next-line no-await-in-loop
    result = await sleep(callback, callbackInput, iterator === 0 ? 0 : pauseTime);
    if (result === '') iterator += 1;
    else iterator = retryCount;
  }
  return result;
};

enum orbcommDeviceTypes {
  PL321 = 'PL321',
  PL121 = 'PL121',
}
enum pl240DeviceTypes {
  PL240 = 'PL240',
  PL240B = 'PL240B',
}
enum pl616DeviceTypes {
  PL161 = 'PL161',
  PL161SC = 'PL161SC',
}

export const testIsOrbcomm = (deviceType: string | undefined | null) =>
  deviceType === orbcommDeviceTypes.PL321 || deviceType === orbcommDeviceTypes.PL121;

export const testIsPL240 = (deviceType: string | undefined | null) =>
  deviceType === pl240DeviceTypes.PL240 || deviceType === pl240DeviceTypes.PL240B;

export const testIsPL161 = (deviceType: string | undefined | null) =>
  deviceType === pl616DeviceTypes.PL161 || deviceType === pl616DeviceTypes.PL161SC;

export const findPrioritizedIndex = (
  arrayOfObjects: any,
  primaryKey: string,
  prioritizedKeys: Array<string>,
) => {
  for (const key of prioritizedKeys) {
    const index = arrayOfObjects.findIndex((node: any) => node[primaryKey] === key);
    if (index !== -1) return index;
  }
  return -1;
};
